import { useParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Printer, Save } from "lucide-react";
import { useInvoice } from "@/lib/queries";
import { useUpdateInvoice } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { InvoiceStatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { createInvoicePdf } from "@/lib/pdf/invoicePdf";
import { printPdf } from "@/lib/print";
import { fmtINR, fmtINRInput, fmtDate } from "@emr/shared/india";
import { useClinicDoctors } from "@/lib/queries";
import { toast } from "sonner";

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useInvoice(id);
  const { data: doctorsData } = useClinicDoctors();
  const updateMut = useUpdateInvoice(id ?? "");
  const [paidRupees, setPaidRupees] = useState("");

  if (isLoading) {
    return <div className="p-6"><Skeleton className="h-64" /></div>;
  }
  const inv = data?.invoice;
  if (!inv) return <div className="p-6 text-muted">Invoice not found.</div>;

  const doctor = inv.createdByDoctorId ? (doctorsData && !Array.isArray(doctorsData) ? (doctorsData as { rows?: { id: string; name: string; regNumber: string | null }[] })?.rows?.find((d) => d.id === inv.createdByDoctorId) : undefined) : undefined;

  const print = async () => {
    const doc = await createInvoicePdf({
      number: inv.number,
      date: inv.date,
      patientName: inv.patientName ?? "Patient",
      clinicUhid: inv.clinicUhid ?? null,
      patientPhone: inv.patientPhone ?? null,
      lineItems: inv.lineItems,
      subtotalPaise: inv.subtotalPaise,
      totalPaise: inv.totalPaise,
      paidPaise: inv.paidPaise,
      status: inv.status,
      doctorName: doctor?.name ?? null,
      doctorRegNumber: doctor?.regNumber ?? null,
    });
    printPdf(doc);
  };

  const recordPayment = async () => {
    const amountPaise = Math.round((parseFloat(paidRupees) || 0) * 100);
    if (amountPaise <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    const newPaid = inv.paidPaise + amountPaise;
    const status = newPaid >= inv.totalPaise ? "Paid" : "Partial";
    try {
      await updateMut.mutateAsync({ paidPaise: newPaid, status });
      setPaidRupees("");
    } catch {
      // handled
    }
  };

  const balance = inv.totalPaise - inv.paidPaise;

  return (
    <div className="mx-auto max-w-4xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/billing")}><ArrowLeft size={18} /></Button>
          <div>
            <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Invoice {inv.number}</h1>
            <div className="text-sm text-muted">{inv.patientName ?? "—"} · {inv.clinicUhid ?? "—"} · {fmtDate(inv.date)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <InvoiceStatusBadge status={inv.status} />
          <Button variant="outline" size="sm" onClick={print}><Printer size={14} className="mr-1" /> Print</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-maroon-700 dark:text-amber-500">Line items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 dark:bg-dark-700">
                <TableHead>Sr #</TableHead>
                <TableHead>Service</TableHead>
                <TableHead className="w-20">Qty</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.lineItems.map((item, idx) => (
                <TableRow key={idx} className="even:bg-cream-50/50">
                  <TableCell className="font-mono text-xs">{idx + 1}</TableCell>
                  <TableCell className="text-sm">{item.name}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell className="text-right">{fmtINR(item.amountPaise * item.quantity)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <div className="w-72 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted">Subtotal</span><span>{fmtINR(inv.subtotalPaise)}</span></div>
          <div className="flex justify-between text-base font-bold text-maroon-700 dark:text-amber-500"><span>Total</span><span>{fmtINR(inv.totalPaise)}</span></div>
          <div className="flex justify-between"><span className="text-muted">Paid</span><span className="text-green-700">{fmtINR(inv.paidPaise)}</span></div>
          <div className="flex justify-between"><span className="text-muted">Balance</span><span className="text-red-600">{fmtINR(balance)}</span></div>
        </div>
      </div>

      {balance > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-maroon-700 dark:text-amber-500">Record payment</CardTitle></CardHeader>
          <CardContent className="flex items-end gap-3">
            <div className="flex-1">
              <Label>Amount received (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={paidRupees}
                onChange={(e) => setPaidRupees(e.target.value)}
                placeholder={fmtINRInput(balance)}
              />
            </div>
            <Button onClick={recordPayment} disabled={updateMut.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              <Save size={14} className="mr-1" /> Record
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
